import { useTranslation } from "react-i18next";
import type { SubscriptionState } from "../lib/subscription-access";
import { formatDateAbonnement, libelleEtatAbonnement } from "../lib/subscription-labels";

/**
 * Badge d'état d'un abonnement : « Résilié — accès jusqu'au … », « En pause — reprise le … ».
 *
 * Avant le 29/09/2026, la carte d'abonnement DISPARAISSAIT dès que le statut quittait
 * « actif » : le client résilié se retrouvait devant l'ancienne vue « pool de licences », sans
 * la moindre indication de ce qui s'était passé ni de la date de fin de son accès.
 *
 * L'état vient du serveur (`access`, calculé par `subscription-access.ts`) : cet écran affiche,
 * il ne re-décide pas.
 */
export interface SubscriptionAccessView {
  isOpen: boolean;
  state: SubscriptionState;
  accessEndsAt?: string | Date | null;
  resumesAt?: string | Date | null;
}

/** Couleurs de la charte : vert menthe (en cours), jaune pastel (en sursis), corail (terminé). */
const COULEURS: Record<SubscriptionState, { fond: string; texte: string }> = {
  active: { fond: "#E8F4EC", texte: "#1E7A3C" },
  trial: { fond: "#E8F4EC", texte: "#1E7A3C" },
  cancelled: { fond: "#FDF3D7", texte: "#926B00" },
  non_renewing: { fond: "#FDF3D7", texte: "#926B00" },
  unpaid: { fond: "#FDF3D7", texte: "#926B00" },
  paused: { fond: "#EAF3FD", texte: "#384872" },
  ended: { fond: "#FDECEB", texte: "#C0423F" },
};

export function SubscriptionStateBadge({ access }: { access?: SubscriptionAccessView | null }) {
  const { t, i18n } = useTranslation();
  if (!access) return null;

  const { state, isOpen } = access;
  // Un abonnement en cours n'a pas besoin de badge : c'est le cas normal.
  if (state === "active" || state === "trial") return null;

  const finLabel = formatDateAbonnement(access.accessEndsAt, i18n.language);
  const repriseLabel = formatDateAbonnement(access.resumesAt, i18n.language);
  // Libellé calculé par la source unique : le bandeau d'accueil lit la MÊME fonction.
  const texte = libelleEtatAbonnement(
    { state, isOpen, finFormatee: finLabel, repriseFormatee: repriseLabel },
    t,
  );

  const couleur = COULEURS[state] ?? COULEURS.ended;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
      style={{ backgroundColor: couleur.fond, color: couleur.texte }}
    >
      {texte}
    </span>
  );
}
